import { useEffect, useState, type ReactNode } from 'react';
import { SectionIcon, type Section } from './icons';
import { EXPLORE_SLIDES } from '../lib/exploreSlides';

/**
 * The band at the top of an Explore section.
 *
 * Find Your Next Home has had one since the day it shipped — a deep blue band,
 * a sentence, a search box — and Services and Marketplace had a heading on a
 * white page. Same product, two standards, and the two newer sections read as
 * the unfinished ones. §33 asks the three to share a visual language; this is
 * the top half of that.
 *
 * IT ROTATES, and what rotates is the SENTENCE as much as the picture. A
 * visitor arriving at Services does not know what is in there — "Mama Fua",
 * "gas delivery", "a fundi for the leaking tap" tells them in three seconds
 * what a category list takes a minute to. Each slide is one line of that,
 * changed every seven seconds, paused entirely for anybody who has asked for
 * reduced motion (the whole point is legibility, and a caption nobody can
 * finish reading is worse than a static one).
 *
 * PHOTOGRAPHS ARE OPTIONAL. A slide may name an image in web/public/explore;
 * where there is none, the band is the section's own colour and still looks
 * deliberate. That is not a placeholder — it is the design working with what
 * exists — so shipping the copy today and adding photographs later changes the
 * page without ever leaving it broken. Adding one is a filename in
 * lib/exploreSlides.ts and a file in the folder.
 */

export function ExploreHero(
  { section, title, sub, children }: {
    section: Section;
    title: string;
    sub: string;
    /** The search row, rendered inside the band under the copy. */
    children?: ReactNode;
  },
) {
  const slides = EXPLORE_SLIDES[section] ?? [];
  const [at, setAt] = useState(0);

  useEffect(() => {
    if (slides.length < 2) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const t = window.setInterval(() => setAt(i => (i + 1) % slides.length), 7000);
    return () => window.clearInterval(t);
  }, [slides.length]);

  const slide = slides[at];

  return (
    <section className={`xhero xhero-${section}`}>
      {/* Photographs stack behind the copy and cross-fade. Rendering all of
          them and toggling opacity — rather than swapping one src — means the
          browser has already decoded the next one, so a slide change is not a
          flash of empty band on a slow connection. */}
      {/* Keyed by POSITION, not by filename: two slides may legitimately share
          a photograph, and keying on the url collapsed them into one node that
          then belonged to whichever slide rendered first. */}
      {slides.map((s, i) => (
        s.image
          ? <div key={i} aria-hidden
              className={`xhero-shot${i === at ? ' on' : ''}`}
              style={{ backgroundImage: `url(${s.image})` }} />
          : null
      ))}

      <div className="xhero-inner">
        {/* Flat, because the badge IS the tile here — a tinted tile inside a
            translucent one is two boxes where the design has one. */}
        <span className="xhero-badge"><SectionIcon section={section} size={20} flat /></span>
        <h1 className="xhero-title">{title}</h1>
        <p className="xhero-sub">{sub}</p>

        {slide && (
          /* aria-live so somebody using a screen reader is told the line
             changed, polite so it waits for them to finish what they are on. */
          <p className="xhero-line" aria-live="polite">{slide.line}</p>
        )}

        {children}

        {slides.length > 1 && (
          <div className="xhero-dots" role="tablist" aria-label="Highlights">
            {slides.map((s, i) => (
              <button key={i} type="button" role="tab"
                aria-selected={i === at} aria-label={s.line}
                className={`xhero-dot${i === at ? ' on' : ''}`}
                onClick={() => setAt(i)} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
